import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, LifeBuoy, Mail, Send, Loader2, ChevronDown, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LuvmeIcon from "@/components/LuvmeIcon";
import { toast } from "sonner";

const SUPPORT_EMAIL = "supporto@luvme.com";

const FAN_FAQS = [
  { q: "Come funzionano i pagamenti e gli acquisti su Luvme?", a: "Puoi abbonarti a un creator (pagamento mensile ricorrente) oppure sbloccare singoli contenuti a pagamento (PPV) con un acquisto una tantum. I pagamenti avvengono in modo sicuro sulla piattaforma. In questa versione demo i pagamenti sono simulati, senza addebiti reali." },
  { q: "Come mi abbono a un creator?", a: "Apri il profilo pubblico del creator e premi 'Abbonati'. Dopo la conferma del pagamento avrai accesso immediato a tutti i suoi contenuti riservati agli abbonati, finché l'abbonamento è attivo." },
  { q: "Come annullo un abbonamento e cosa succede al mio accesso?", a: "Vai in 'Abbonamenti', scegli il creator e premi 'Annulla abbonamento'. Manterrai l'accesso ai contenuti fino alla fine del periodo già pagato: alla scadenza il rinnovo automatico non verrà più addebitato." },
  { q: "Come riattivo un abbonamento che ho annullato?", a: "Sempre dalla pagina 'Abbonamenti', sugli abbonamenti annullati ma ancora attivi trovi il pulsante 'Riattiva': il rinnovo automatico riprenderà dalla scadenza corrente." },
  { q: "Come sblocco un contenuto singolo (PPV)?", a: "Nei post bloccati vedrai un lucchetto fucsia con il prezzo. Premi 'Sblocca', conferma il pagamento e il contenuto sarà tuo per sempre, anche senza abbonamento." },
  { q: "Cos'è la classifica fan e come mi metto in evidenza?", a: "La 'Classifica' include una sezione 'Top Fan' che premia con badge (Bronzo, Argento, Oro, Diamante) chi sostiene di più i creator. Dalla stessa pagina, come fan, puoi acquistare la promozione 'Mettiti in evidenza' per apparire in cima alla classifica." },
  { q: "Perché mi viene richiesta la verifica dell'età?", a: "Luvme è riservato ai maggiorenni. In fase di registrazione confermi di avere almeno 18 anni: è un requisito obbligatorio per la sicurezza e la conformità della piattaforma." },
  { q: "Come contatto il supporto?", a: `Scrivici con il form qui sotto oppure invia una mail a ${SUPPORT_EMAIL}. Ti rispondiamo entro 24-48 ore.` },
];

const CREATOR_FAQS = [
  { q: "Come vengono calcolati i miei guadagni e la commissione?", a: "Luvme trattiene solo il 15% di commissione: l'85% netto di ogni abbonamento e sblocco va a te. In Dashboard vedi in tempo reale lordo incassato, commissione e netto, con lo storico di tutte le transazioni." },
  { q: "Come pubblico contenuti a pagamento?", a: "Dalla Dashboard usa 'Nuovo post', carica immagine o video e attiva 'Contenuto a pagamento' impostando un prezzo di sblocco. Puoi pubblicare contenuti a pagamento solo dopo aver completato la verifica identità; i contenuti gratuiti sono sempre consentiti." },
  { q: "Come funziona la verifica identità e perché è obbligatoria?", a: "Dalla pagina 'Il mio profilo' carichi documento (fronte/retro) e un selfie. Il team li revisiona: una volta approvati ottieni il badge verificato e sblocchi la pubblicazione di contenuti a pagamento. È obbligatoria per garantire sicurezza e conformità." },
  { q: "Come modifico o elimino un mio post?", a: "Nella sezione 'I tuoi post' della Dashboard, passa sul contenuto e usa le icone Modifica (per cambiare didascalia, prezzo e stato gratuito/a pagamento) o Elimina (con conferma prima della cancellazione definitiva)." },
  { q: "Come richiedo un prelievo dei miei guadagni?", a: "Dalla Dashboard, nella sezione 'Guadagni', trovi il tuo saldo netto e il pulsante di prelievo: i fondi vengono trasferiti sul metodo di pagamento collegato al tuo profilo." },
  { q: "Come promuovo (sponsorizzo) il mio profilo?", a: "Nella Dashboard, sezione 'Promuovi il tuo profilo', acquisti uno slot di visibilità (24h o 7 giorni) e appari nella sezione 'Sponsorizzato' del Feed. Quando è attiva puoi vedere il tempo rimanente, estenderla o annullarla in anticipo." },
  { q: "Come imposto il prezzo dell'abbonamento e le categorie?", a: "Da 'Il mio profilo' scegli il tipo di profilo (gratuito o ad abbonamento), imposti il prezzo mensile e selezioni fino a 2 categorie: questo aiuta i fan a scoprirti tramite i filtri del Feed." },
  { q: "Come funziona il programma referral?", a: "Invitando altri creator ottieni una commissione ridotta all'8% per 3 mesi; i fan che inviti ricevono un credito di benvenuto. Trovi il tuo codice/link di invito nel profilo e in Dashboard." },
  { q: "Come contatto il supporto?", a: `Scrivici con il form qui sotto oppure invia una mail a ${SUPPORT_EMAIL}. Ti rispondiamo entro 24-48 ore.` },
];

const GENERAL_FAQS = [
  { q: "Cos'è Luvme e come funziona?", a: "Luvme è una piattaforma che mette in contatto creator e fan: i creator condividono contenuti gratuiti e riservati, i fan si abbonano o sbloccano singoli contenuti. La commissione della piattaforma è solo del 15%." },
  { q: "Come mi registro, come creator o come fan?", a: "Premi 'Registrati' e scegli il tuo ruolo. La registrazione richiede la conferma di avere almeno 18 anni e l'accettazione dei Termini di Servizio." },
  { q: "Come funzionano i pagamenti?", a: "Abbonamenti mensili e sblocchi singoli avvengono in modo sicuro sulla piattaforma. In questa versione demo i pagamenti sono simulati." },
  { q: "Luvme è sicuro? Verifica di età e identità.", a: "L'accesso è riservato ai maggiorenni e i creator completano una verifica dell'identità prima di pubblicare contenuti a pagamento, per la massima sicurezza di tutti." },
  { q: "Come contatto il supporto?", a: `Scrivici con il form qui sotto oppure invia una mail a ${SUPPORT_EMAIL}. Ti rispondiamo entro 24-48 ore.` },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl bg-[#1A1A1A] overflow-hidden" data-testid="faq-item">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-medium text-sm">{q}</span>
        <ChevronDown className={`w-4 h-4 text-white/50 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-white/60 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.display_name || "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/support/contact", form);
      toast.success(data.message || "Messaggio inviato!");
      setForm({ name: user?.display_name || "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invio fallito");
    } finally {
      setSending(false);
    }
  };

  const faqs = user?.role === "creator" ? CREATOR_FAQS : user?.role === "fan" ? FAN_FAQS : GENERAL_FAQS;

  const content = (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-black font-heading mb-1 flex items-center gap-2">
        <LifeBuoy className="w-7 h-7 text-[#E91E8C]" /> Assistenza
      </h1>
      <p className="text-white/50 mb-8 text-sm">Siamo qui per aiutarti. Consulta le domande frequenti o scrivici.</p>

      <h2 className="text-lg font-bold font-heading mb-3">
        {user?.role === "creator" ? "Domande frequenti per i creator" : user?.role === "fan" ? "Domande frequenti per i fan" : "Domande frequenti"}
      </h2>
      <div className="space-y-2.5 mb-10" data-testid="faq-list">
        {faqs.map((f, i) => <FaqItem key={i} {...f} />)}
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-6">
        <h2 className="text-lg font-bold font-heading mb-1">Contattaci</h2>
        <p className="text-sm text-white/50 mb-4">
          Oppure scrivici a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#E91E8C] font-medium" data-testid="support-email"><Mail className="w-3.5 h-3.5 inline mr-1" />{SUPPORT_EMAIL}</a>
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="support-form">
          <div>
            <Label className="text-white/80">Nome</Label>
            <Input value={form.name} onChange={set("name")} required data-testid="support-name" placeholder="Il tuo nome" className="mt-2 bg-[#0D0D0D] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]" />
          </div>
          <div>
            <Label className="text-white/80">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} required data-testid="support-email-input" placeholder="tu@esempio.com" className="mt-2 bg-[#0D0D0D] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]" />
          </div>
          <div>
            <Label className="text-white/80">Messaggio</Label>
            <textarea value={form.message} onChange={set("message")} required rows={4} data-testid="support-message" placeholder="Come possiamo aiutarti?" className="mt-2 w-full bg-[#0D0D0D] border border-white/10 rounded-xl p-3 text-sm focus:border-[#E91E8C] focus:outline-none" />
          </div>
          <Button type="submit" disabled={sending} data-testid="support-submit" className="h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] font-semibold gap-2 px-6">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Invia messaggio</>}
          </Button>
        </form>
      </div>
    </div>
  );

  if (user) {
    return (
      <div className="flex bg-[#0D0D0D] min-h-screen text-white">
        <AppNav />
        <main className="flex-1 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">{content}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-6">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="support-logo">
            <LuvmeIcon className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-xl font-black font-heading">Luvme</span>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
        {content}
      </div>
    </div>
  );
}
